import { useSetEventTestClock } from "@/api/admin/useSetEventTestClock";
import { useUpdateEventTestMode } from "@/api/admin/useUpdateEventTestMode";
import {
  EventTestClockDto,
  EventTestClockStage,
  ReservationRequestDto,
} from "@/api/generated/generatedTypes";
import {
  Alert,
  Badge,
  Button,
  Group,
  List,
  Loader,
  Modal,
  Paper,
  Stack,
  Switch,
  Text,
  Timeline,
} from "@mantine/core";
import { useState } from "react";
import { showErrorNotification, showSuccessNotification } from "../utils/errorNotifications";

type ConfirmAction = "ENABLE_TEST_MODE" | "DISABLE_TEST_MODE" | "RELEASE_ALL";

interface ConfirmContent {
  title: string;
  message: string;
  confirmLabel: string;
  confirmColor: string;
  showsAttendees: boolean;
}

const CONFIRM_CONTENT: Record<ConfirmAction, ConfirmContent> = {
  ENABLE_TEST_MODE: {
    title: "테스트 이벤트로 지정할까요?",
    message:
      "테스트 이벤트는 시뮬레이션 시간을 앞당겨 공개 콘텐츠를 미리 열고, 예약된 문자를 참석자에게 즉시 발송할 수 있습니다. 실제 게스트가 있는 이벤트라면 지정하지 마세요.",
    confirmLabel: "테스트 이벤트로 지정",
    confirmColor: "orange",
    showsAttendees: true,
  },
  DISABLE_TEST_MODE: {
    title: "테스트 모드를 해제할까요?",
    message:
      "시뮬레이션 시간이 초기화되고 실시간으로 돌아갑니다. 아직 공개 시간이 되지 않은 콘텐츠는 다시 잠기고, 발송되지 않은 문자는 원래 예약된 시간에 발송됩니다. 이미 발송된 문자는 취소되지 않습니다.",
    confirmLabel: "테스트 모드 해제",
    confirmColor: "gray",
    showsAttendees: false,
  },
  RELEASE_ALL: {
    title: "전체 공개할까요?",
    message:
      "모든 공개 콘텐츠가 열리고, 아직 발송되지 않은 예약 문자가 아래 참석자 전원에게 즉시 발송됩니다. 발송된 문자는 취소할 수 없습니다.",
    confirmLabel: "전체 공개",
    confirmColor: "red",
    showsAttendees: true,
  },
};

interface StageInfo {
  stage: EventTestClockStage;
  title: string;
  description: string;
}

const STAGES: StageInfo[] = [
  {
    stage: EventTestClockStage.GUEST_HINTS_REVEALED,
    title: "전날 16:20 · 게스트 힌트 공개",
    description: "게스트 메시지, 직업·국적 분포, 힌트 안내 문자",
  },
  {
    stage: EventTestClockStage.RESTAURANT_REVEALED,
    title: "당일 07:30 · 레스토랑 공개",
    description: "레스토랑 이름·주소, 레스토랑 안내 문자",
  },
  {
    stage: EventTestClockStage.CURATOR_CONTENT_REVEALED,
    title: "당일 18:00 · 큐레이터 노트 공개",
    description: "큐레이터 메시지, 토킹 포인트 카드, 안내 문자",
  },
  {
    stage: EventTestClockStage.EVENT_STARTED,
    title: "당일 19:00 · 이벤트 시작",
    description: "지각·취소 참석자 안내 문자",
  },
  {
    stage: EventTestClockStage.AFTER_PARTY_REVEALED,
    title: "당일 20:15 · 애프터파티 공개",
    description: "애프터파티 장소, 애프터파티 안내 문자",
  },
  {
    stage: EventTestClockStage.FEEDBACK_REQUESTED,
    title: "당일 22:00 · 피드백 오픈",
    description: "피드백 요청 문자, 피드백 폼, 참석자 평가 오픈",
  },
];

const LAST_STAGE = EventTestClockStage.FEEDBACK_REQUESTED;

const formatKoreaTime = (time: string) =>
  new Date(time).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" });

interface EventTestClockPanelProps {
  eventId: number;
  testClock: EventTestClockDto;
  attendees: ReservationRequestDto[];
}

export const EventTestClockPanel = ({
  eventId,
  testClock,
  attendees,
}: EventTestClockPanelProps) => {
  const updateTestModeMutation = useUpdateEventTestMode();
  const setTestClockMutation = useSetEventTestClock();
  const isPending = updateTestModeMutation.isPending || setTestClockMutation.isPending;
  const isStagePending = (stage: EventTestClockStage | null) =>
    setTestClockMutation.isPending && setTestClockMutation.variables?.stage === stage;
  const [pendingConfirmation, setPendingConfirmation] = useState<ConfirmAction | null>(null);

  const activeStageIndex = STAGES.findIndex(({ stage }) => stage === testClock.stage);

  const handleTestModeChange = async (isTest: boolean) => {
    try {
      await updateTestModeMutation.mutateAsync({ eventId, isTest });
      showSuccessNotification(
        "테스트 모드 변경",
        isTest ? "테스트 이벤트로 지정되었습니다." : "실제 이벤트로 되돌렸습니다."
      );
    } catch (error) {
      showErrorNotification(error, "테스트 모드 변경 실패");
    }
  };

  const handleStageButtonClick = (stage: EventTestClockStage) => {
    if (
      !window.confirm(
        "이 시점까지 예약된 문자가 이 이벤트의 모든 참석자에게 즉시 발송됩니다. 계속하시겠습니까?"
      )
    ) {
      return;
    }

    applyStage(stage);
  };

  const handleConfirm = () => {
    const action = pendingConfirmation;
    setPendingConfirmation(null);

    switch (action) {
      case "ENABLE_TEST_MODE":
        handleTestModeChange(true);
        break;
      case "DISABLE_TEST_MODE":
        handleTestModeChange(false);
        break;
      case "RELEASE_ALL":
        applyStage(LAST_STAGE);
        break;
      default:
        break;
    }
  };

  const applyStage = async (stage: EventTestClockStage | null) => {
    try {
      const result = await setTestClockMutation.mutateAsync({ eventId, stage });
      showSuccessNotification(
        "테스트 시간 변경",
        stage
          ? `시뮬레이션 시간이 변경되었습니다. 문자 ${result.releasedNotificationCount}건이 발송되었습니다.`
          : "실시간으로 되돌렸습니다."
      );
    } catch (error) {
      showErrorNotification(error, "테스트 시간 변경 실패");
    }
  };

  return (
    <Paper withBorder p="sm" radius="md" bg={testClock.isTest ? "yellow.0" : undefined}>
      <Stack gap="sm">
        <Group justify="space-between">
          <Group gap="xs">
            <Switch
              label="테스트 이벤트"
              checked={testClock.isTest}
              disabled={isPending}
              onChange={(event) =>
                setPendingConfirmation(
                  event.currentTarget.checked ? "ENABLE_TEST_MODE" : "DISABLE_TEST_MODE"
                )
              }
            />
            {updateTestModeMutation.isPending && <Loader size="xs" />}
          </Group>
          {testClock.isTest && (
            <Badge color={testClock.simulatedNow ? "orange" : "gray"}>
              {testClock.simulatedNow
                ? `시뮬레이션: ${formatKoreaTime(testClock.simulatedNow)}`
                : "실시간"}
            </Badge>
          )}
        </Group>

        {testClock.isTest && (
          <>
            <Text size="xs" c="dimmed">
              선택한 시점까지의 공개 콘텐츠가 열리고, 예약된 문자는 즉시 발송됩니다. 시간을 되돌리면
              콘텐츠는 다시 잠기지만 이미 발송된 문자는 취소되지 않습니다.
            </Text>

            <Timeline active={activeStageIndex} bulletSize={16} lineWidth={2}>
              {STAGES.map(({ stage, title, description }) => (
                <Timeline.Item key={stage} title={<Text size="sm">{title}</Text>}>
                  <Group justify="space-between" wrap="nowrap">
                    <Text size="xs" c="dimmed">
                      {description}
                    </Text>
                    <Button
                      size="compact-xs"
                      variant={stage === testClock.stage ? "filled" : "light"}
                      loading={isStagePending(stage)}
                      disabled={isPending || stage === testClock.stage}
                      onClick={() => handleStageButtonClick(stage)}
                    >
                      이동
                    </Button>
                  </Group>
                </Timeline.Item>
              ))}
            </Timeline>

            <Group justify="flex-end" gap="xs">
              <Button
                size="xs"
                variant="outline"
                color="gray"
                loading={isStagePending(null)}
                disabled={isPending || !testClock.simulatedNow}
                onClick={() => applyStage(null)}
              >
                실시간으로 되돌리기
              </Button>
              <Button
                size="xs"
                color="orange"
                loading={isStagePending(LAST_STAGE)}
                disabled={isPending || testClock.stage === LAST_STAGE}
                onClick={() => setPendingConfirmation("RELEASE_ALL")}
              >
                전체 공개
              </Button>
            </Group>
          </>
        )}
      </Stack>

      <Modal
        opened={pendingConfirmation !== null}
        onClose={() => setPendingConfirmation(null)}
        title={
          <Text fw={700} c="black">
            {pendingConfirmation && CONFIRM_CONTENT[pendingConfirmation].title}
          </Text>
        }
        centered
        styles={{ body: { color: "black" } }}
      >
        {pendingConfirmation && (
          <Stack gap="md">
            <Alert color={CONFIRM_CONTENT[pendingConfirmation].confirmColor} variant="light">
              <Text size="sm">{CONFIRM_CONTENT[pendingConfirmation].message}</Text>
            </Alert>

            {CONFIRM_CONTENT[pendingConfirmation].showsAttendees && (
              <div>
                <Text size="sm" fw={700} mb="xs">
                  문자를 받을 참석자 ({attendees.length}명)
                </Text>
                {attendees.length > 0 ? (
                  <List size="sm" spacing={2}>
                    {attendees.map((attendee) => (
                      <List.Item key={attendee.id}>
                        {attendee.user.nickname || attendee.user.name || "이름 없음"}
                        {attendee.user.name && attendee.user.nickname
                          ? ` (${attendee.user.name})`
                          : ""}
                      </List.Item>
                    ))}
                  </List>
                ) : (
                  <Text size="sm" c="dimmed">
                    참석자가 없습니다.
                  </Text>
                )}
              </div>
            )}

            <Group justify="flex-end" gap="xs">
              <Button variant="default" onClick={() => setPendingConfirmation(null)}>
                취소
              </Button>
              <Button
                color={CONFIRM_CONTENT[pendingConfirmation].confirmColor}
                onClick={handleConfirm}
              >
                {CONFIRM_CONTENT[pendingConfirmation].confirmLabel}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Paper>
  );
};
